import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AttributeInfo,
  NetworkInfo,
  OldNetworkInfo
} from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { awaitJSON, StreamingV2LambdaFetch } from '@src/utils/fetch-utils';
import { ContentMetaDataFields, StreamingNetworkRecord } from './streaming-networks-constants';
import { mergeNetworkRecords } from './streaming-networks-utils';

// `streamingv2` has two list endpoints backing what used to be an experiment-flagged old/new split.
// Neither alone has the full record the page needs (see the comment on StreamingNetworkRecord), so
// this hook fetches both and merges them client-side — confirmed safe: the underlying tables are
// independent, so calling both is not mutually exclusive at the storage layer.
export const useStreamingNetworks = (): {
  data?: StreamingNetworkRecord[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  // Attributes only live on the old endpoint's response — a dedicated refetch avoids pointlessly
  // re-fetching the new endpoint too after every attribute save.
  refetchOld: () => Promise<unknown>;
} => {
  const oldQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingNetworks', 'old'],
    queryFn: async (): Promise<OldNetworkInfo[]> =>
      awaitJSON<OldNetworkInfo[]>(await StreamingV2LambdaFetch('/old_streaming_networks'))
  });
  const newQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingNetworks', 'new'],
    queryFn: async (): Promise<NetworkInfo[]> =>
      awaitJSON<NetworkInfo[]>(await StreamingV2LambdaFetch('/streaming_networks'))
  });

  const data = useMemo(() => {
    if (!oldQuery.data && !newQuery.data) return undefined;
    const oldByCode = new Map((oldQuery.data ?? []).map(network => [network.shortCode, network]));
    const newByCode = new Map((newQuery.data ?? []).map(network => [network.shortCode, network]));
    const allCodes = new Set([...oldByCode.keys(), ...newByCode.keys()]);
    return [...allCodes]
      .map(code => mergeNetworkRecords(oldByCode.get(code), newByCode.get(code)))
      .filter((network): network is StreamingNetworkRecord => Boolean(network));
  }, [oldQuery.data, newQuery.data]);

  return {
    data,
    isLoading: oldQuery.isLoading || newQuery.isLoading,
    error: oldQuery.error ?? newQuery.error,
    refetch: () => Promise.all([oldQuery.refetch(), newQuery.refetch()]),
    refetchOld: () => oldQuery.refetch()
  };
};

export const useDspOptions = (): ReturnType<typeof useQuery<string[]>> =>
  useQuery({
    queryKey: ['streamingNetworkDspOptions'],
    queryFn: async (): Promise<string[]> =>
      awaitJSON<string[]>(await StreamingV2LambdaFetch('/getDspOptions'))
  });

// Global attribute-schema catalog — attribute names/types/multi-select options, shared by the
// Attributes tab (to know what can be added to a network) and the attribute-schema admin modal (to
// define new ones). Per-network attribute *values* live on StreamingNetworkRecord.attributes instead.
export interface AttributeSchema extends AttributeInfo {
  id: number;
}

export const useAttributeSchema = (): ReturnType<
  typeof useQuery<Record<string, AttributeSchema>>
> =>
  useQuery({
    queryKey: ['streamingNetworkAttributeSchema'],
    queryFn: async (): Promise<Record<string, AttributeSchema>> =>
      awaitJSON<Record<string, AttributeSchema>>(
        await StreamingV2LambdaFetch('/get_global_streaming_network_properties')
      )
  });

// ---- save helpers (imperative POST; caller refetches the relevant query on success) ----

export const saveNetwork = async (combinedData: StreamingNetworkRecord): Promise<void> => {
  await StreamingV2LambdaFetch('/update_streaming_networks', {
    method: 'POST',
    body: { combinedData }
  });
};

export interface AddNetworkParams {
  shortCode: string;
  networkGroup: string;
  name: string;
  color: string;
  buyable: boolean;
  cpm: number | null;
  defaultIoPdfText: string;
  pdfText: string;
  requiresDescriptions: boolean;
  platforms: { platform: string; buyable: boolean }[];
  dsp: string[];
}

export const addNetwork = async (params: AddNetworkParams): Promise<void> => {
  await StreamingV2LambdaFetch('/add_streaming_network', { method: 'POST', body: params });
};

interface AttributeEditsParams {
  networkInfo: { network: string; platform: string; description: string };
  edits: Record<string, AttributeInfo>;
}

export const saveNetworkAttributes = async (params: AttributeEditsParams): Promise<void> => {
  await StreamingV2LambdaFetch('/save_network_attributes', { method: 'POST', body: params });
};

// The backend's WHERE clause matches on network + content + platform_prefix as separate columns —
// sending a composite "content_platformPrefix" string as `content` with no `platformPrefix` never
// matches a real row, so that save would silently update nothing. Send the real values.
export const saveNetworkContentMetaData = async (
  network: string,
  content: string,
  platformPrefix: string,
  edits: ContentMetaDataFields
): Promise<void> => {
  await StreamingV2LambdaFetch('/update_network_content_meta_data', {
    method: 'POST',
    body: { network, content, platformPrefix, combinedData: edits }
  });
};

export interface NewAttributeSchemaParams {
  newAttributes: Record<string, { name: string; type: string; options: string[]; new: true }>;
  newOptions: Record<string, string[]>;
}

export const saveAttributeSchema = async (params: NewAttributeSchemaParams): Promise<void> => {
  await StreamingV2LambdaFetch('/add_global_streaming_network_properties', {
    method: 'POST',
    body: params
  });
};
